import { memo, useEffect } from "react";
import { useAtom, useSetAtom } from "jotai";
import { mapDataListAtom, mapDataDisplayedBoundsAtom, allDisplayedDataBoundsAtom } from "../stores/mapStore";
import { LngLatBounds } from "maplibre-gl";
import MapDataLayerPopup from "./popups/MapDataLayerPopup";
import { useAtomValue } from 'jotai';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { remote_mapDataFile_getInfo } from '~/common/typescript/apis/MapDataFileAPIs';
import { parseBoundingBox } from "../panels/LegendPanel/LegendTab/LayerItem/LayerItemUtil";

const MapDataLayerController = () => {
  const currentProject = useAtomValue(currentProjectAtom);
  const [mapDataList] = useAtom(mapDataListAtom);
  const setMapDataDisplayedBounds = useSetAtom(mapDataDisplayedBoundsAtom);

  // Helper function to get bounds from PMTiles or MBTiles
  const getBoundsFromTileFile = async (mapData: any): Promise<string | null> => {
    try {
      const tileFile = mapData.map_data_files?.find(
        (file: any) => file.type === 'pmtiles' || file.type === 'mbtiles'
      );
      if (!tileFile) {
        return null;
      }

      const tilePath = tileFile.path;
      const tileInfo = await remote_mapDataFile_getInfo(tilePath);

      if (tileInfo && tileInfo.bounds && Array.isArray(tileInfo.bounds) && tileInfo.bounds.length >= 4) {
        const bounds = tileInfo.bounds;
        // bounds format: [minLon, minLat, maxLon, maxLat]
        return `${bounds[0]},${bounds[1]},${bounds[2]},${bounds[3]}`;
      }

      return null;
    } catch (error) {
      console.warn('MapDataLayerController - Error getting bounds from tile file:', error);
      return null;
    }
  };

  useEffect(() => {
    const processMapDataList = async () => {
      // Chỉ tính toán bounds cho các layer đang hiển thị (visible: true)
      const visibleMapDataList = mapDataList?.filter((mapData) => mapData && (mapData as any).visible);

      if (!visibleMapDataList || visibleMapDataList.length === 0) {
        setMapDataDisplayedBounds(null);
        return;
      }

      let combinedMapDataBounds: LngLatBounds | null = null;
      let foundValidBounds = false;

      for (const mapData of visibleMapDataList) {
        // Ưu tiên: iqr_bbox (chống outlier) -> metadata.bbox -> direct boundingBox -> gdalInfo -> ogrInfo -> tile file
        let boundingBox: string | null = null;

        if (mapData.metadata) {
          try {
            const metadata = typeof mapData.metadata === 'string' ? JSON.parse(mapData.metadata) : mapData.metadata;

            // 1. Ưu tiên cao nhất: iqr_bbox
            const iqr = parseBoundingBox(metadata.iqr_bbox);
            if (iqr) {
              boundingBox = `${iqr[0]},${iqr[1]},${iqr[2]},${iqr[3]}`;
            }

            // 2. Ưu tiên kế tiếp: bbox
            if (!boundingBox) {
              const b = parseBoundingBox(metadata.bbox);
              if (b) {
                boundingBox = `${b[0]},${b[1]},${b[2]},${b[3]}`;
              }
            }

            // 3. Direct bounds trên metadata
            if (!boundingBox) {
              const direct = parseBoundingBox(metadata.bounds || metadata.boundingBox);
              if (direct) {
                boundingBox = `${direct[0]},${direct[1]},${direct[2]},${direct[3]}`;
              }
            }

            // Thử gdalInfo trước (raster)
            if (!boundingBox) {
              let gdalInfo: any = null;
              const gdalInfoRaw = metadata.gdalInfo || metadata.gdalinfo;
              if (gdalInfoRaw && typeof gdalInfoRaw === 'string' && gdalInfoRaw.trim() !== '') {
                try {
                  gdalInfo = JSON.parse(gdalInfoRaw);
                } catch {
                  gdalInfo = null;
                }
              } else if (gdalInfoRaw && typeof gdalInfoRaw === 'object') {
                gdalInfo = gdalInfoRaw;
              }

              if (gdalInfo) {
                const gdalMetadata = gdalInfo?.metadata || {};
                const zoomMetadata = gdalMetadata[''] || {};
                if (zoomMetadata.bounds) {
                  boundingBox = zoomMetadata.bounds;
                }
              }
            }

            // Thử ogrInfo sau (vector)
            if (!boundingBox) {
              let ogrInfo: any = null;
              const ogrInfoRaw = metadata.ogrInfo || metadata.ogrinfo;

              if (ogrInfoRaw && typeof ogrInfoRaw === 'string' && ogrInfoRaw.trim() !== '') {
                try {
                  ogrInfo = JSON.parse(ogrInfoRaw);
                } catch {
                  ogrInfo = null;
                }
              } else if (ogrInfoRaw && typeof ogrInfoRaw === 'object') {
                ogrInfo = ogrInfoRaw;
              }

              if (ogrInfo) {
                const layers = ogrInfo?.layers;
                if (layers && layers.length > 0) {
                  const firstLayer = layers[0];
                  // Ưu tiên extent [minX, minY, maxX, maxY]
                  const extent = firstLayer?.geometryFields?.[0]?.extent;
                  if (extent && extent.length === 4) {
                    boundingBox = `${extent[0]},${extent[1]},${extent[2]},${extent[3]}`;
                  } else {
                    const envelope = firstLayer?.geometryFields?.[0]?.envelope;
                    if (envelope && envelope.length === 6) {
                      boundingBox = `${envelope[0]},${envelope[2]},${envelope[1]},${envelope[3]}`;
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.warn('MapDataLayerController - Error parsing metadata:', error);
          }
        }

        // Fallback: direct boundingBox trên record nếu metadata không có
        if (!boundingBox && (mapData as any).boundingBox) {
          const direct = parseBoundingBox((mapData as any).boundingBox);
          if (direct) {
            boundingBox = `${direct[0]},${direct[1]},${direct[2]},${direct[3]}`;
          }
        }

        // Fallback: Thử lấy bounds từ file tile (PMTiles hoặc MBTiles)
        if (!boundingBox) {
          try {
            boundingBox = await getBoundsFromTileFile(mapData);
          } catch (error) {
            console.warn('MapDataLayerController - Failed to get bounds from tile file:', error);
          }
        }

        if (boundingBox && typeof boundingBox === 'string') {
          try {
            const bounds = boundingBox.split(',').map((n) => parseFloat(n.trim()));

            if (bounds.length === 4 && !bounds.some(isNaN)) {
              const [minX, minY, maxX, maxY] = bounds;
              const mapDataBounds = new LngLatBounds([minX, minY], [maxX, maxY]);
              if (combinedMapDataBounds) {
                combinedMapDataBounds.extend(mapDataBounds);
              } else {
                combinedMapDataBounds = mapDataBounds;
              }
              foundValidBounds = true;
            }
          } catch (error) {
            console.warn("MapDataLayerController - Error calculating bounds for mapData:", mapData.id, error);
          }
        }
      }

      if (combinedMapDataBounds && foundValidBounds) {
        const boundsString = JSON.stringify([
          [combinedMapDataBounds.getWest(), combinedMapDataBounds.getSouth()],
          [combinedMapDataBounds.getEast(), combinedMapDataBounds.getNorth()],
        ]);
        setMapDataDisplayedBounds(boundsString);
      } else {
        // Im lặng đặt về null cho background state, KHÔNG hiển thị message popup làm phiền người dùng
        setMapDataDisplayedBounds(null);
      }
    };

    processMapDataList();
  }, [mapDataList, setMapDataDisplayedBounds, currentProject]);

  return (
    <>
      <MapDataLayerPopup />
    </>
  );
};

export default memo(MapDataLayerController);